import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, Flame, Wheat, Calendar, AlertCircle, Info, CheckCircle } from 'lucide-react';

export function BakingSessionGuide() {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <h3 className="text-lg text-white font-medium">{title}</h3>
        {expandedSection === id ? (
          <ChevronUp className="w-5 h-5 text-white/70" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/70" />
        )}
      </button>
      {expandedSection === id && (
        <div className="p-4 bg-white/[0.02] space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl text-white font-brand font-light italic mb-2">Planning Baking Sessions</h2>
        <p className="text-white/80">
          Learn how to create and manage your baking sessions for perfectly timed sourdough bread.
        </p>
      </div>

      <Section id="overview" title="What is a Baking Session?">
        <p className="text-white/80">
          A baking session is your complete bread-making schedule from kneading to baking. Breadlify calculates all the
          timing for you based on your target bake time and chosen fermentation method.
        </p>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white/90 font-medium mb-1">Smart Scheduling</p>
              <p className="text-white/70 text-sm">
                Breadlify works backward from your desired bake time, automatically calculating when to knead,
                when to shape, and when to feed your starter. You'll receive notifications at each milestone.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="creating" title="Creating a New Baking Session">
        <p className="text-white/80 mb-4">
          Click the "+" button in the navigation to start a new session. The process has 5 steps:
        </p>

        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-amber-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center text-sm text-amber-400 font-bold">1</span>
              Session Details
            </h4>

            <div className="space-y-3 ml-9">
              <div>
                <p className="text-white/90 font-medium text-sm mb-1">Session Name</p>
                <p className="text-white/70 text-sm">
                  Give your bake a memorable name to help you track it. Examples: "Saturday Morning Loaf",
                  "Holiday Dinner Bread", "First Attempt"
                </p>
              </div>

              <div>
                <p className="text-white/90 font-medium text-sm mb-1">Target Bake Time</p>
                <p className="text-white/70 text-sm mb-2">
                  When do you want to put your bread in the oven? This is the most important input as all other
                  timings are calculated from this.
                </p>
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-200 text-xs">
                      Minimum 6 hours in advance required. Great bread needs time for proper fermentation!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-blue-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-blue-500/20 rounded-full flex items-center justify-center text-sm text-blue-400 font-bold">2</span>
              Fermentation Process
            </h4>

            <p className="text-white/70 text-sm mb-4 ml-9">
              Choose your fermentation method. Each has different timing and flavor characteristics:
            </p>

            <div className="space-y-3 ml-9">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-start gap-2 mb-2">
                  <Clock className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white font-medium text-sm">Direct Fermentation (6-8 hours)</p>
                </div>
                <p className="text-white/60 text-xs ml-6 mb-2">
                  Both bulk fermentation and proofing happen at room temperature. Fastest method, mild flavor.
                </p>
                <ul className="text-white/60 text-xs ml-6 space-y-1">
                  <li>✓ Best for: Same-day baking, beginners, mild tang</li>
                  <li>✓ When available: Any time (minimum 6 hours before bake time)</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-start gap-2 mb-2">
                  <Flame className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white font-medium text-sm">Cold Bulk Fermentation (18-72 hours)</p>
                </div>
                <p className="text-white/60 text-xs ml-6 mb-2">
                  Bulk fermentation happens in the fridge, then room temperature proofing. Customizable cold time (12-72 hours).
                </p>
                <ul className="text-white/60 text-xs ml-6 space-y-1">
                  <li>✓ Best for: More complex flavor, flexible timing, busy schedules</li>
                  <li>✓ When available: 18+ hours before bake time</li>
                  <li>✓ Default: 24 hours cold bulk (you can adjust)</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-start gap-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white font-medium text-sm">Slow Room Temperature (18-24 hours)</p>
                </div>
                <p className="text-white/60 text-xs ml-6 mb-2">
                  Extended room temperature fermentation with minimal intervention. Maximum flavor development.
                </p>
                <ul className="text-white/60 text-xs ml-6 space-y-1">
                  <li>✓ Best for: Maximum flavor, open crumb, traditional method</li>
                  <li>✓ When available: 18+ hours before bake time</li>
                  <li>✓ Requires: Proper ambient temperature (18-20°C ideal)</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-start gap-2 mb-2">
                  <Flame className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white font-medium text-sm">Cold Retard / Pre-Proofed (18-72 hours)</p>
                </div>
                <p className="text-white/60 text-xs ml-6 mb-2">
                  Room temperature bulk, then extended cold proofing in the fridge. Customizable cold time (12-72 hours).
                </p>
                <ul className="text-white/60 text-xs ml-6 space-y-1">
                  <li>✓ Best for: Convenience (bake straight from fridge), enhanced flavor, improved scoring</li>
                  <li>✓ When available: 18+ hours before bake time</li>
                  <li>✓ Default: 24 hours cold retard (you can adjust)</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded p-3 mt-3 ml-9">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-blue-200 text-xs">
                  If your target bake time is less than 18 hours away, only Direct Fermentation will be available.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-green-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-green-500/20 rounded-full flex items-center justify-center text-sm text-green-400 font-bold">3</span>
              Oven Type
            </h4>

            <div className="space-y-3 ml-9">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white font-medium text-sm mb-2">Domestic Oven</p>
                <p className="text-white/60 text-xs">
                  Regular home oven. Most common option. Use with a dutch oven, baking stone, or steam injection
                  for best results. Default baking time: 45 minutes.
                </p>
              </div>

              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-white font-medium text-sm mb-2">Wood-Fired Oven</p>
                <p className="text-white/60 text-xs mb-2">
                  Traditional wood or gas-fired oven. If selected, you can specify:
                </p>
                <ul className="text-white/60 text-xs ml-4 space-y-1">
                  <li>• Floor temperature (°C)</li>
                  <li>• Dome temperature (°C)</li>
                  <li>• Baking duration (typically 20-30 minutes)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-purple-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-purple-500/20 rounded-full flex items-center justify-center text-sm text-purple-400 font-bold">4</span>
              Select Starter
            </h4>

            <p className="text-white/70 text-sm mb-3 ml-9">
              Choose which starter to use for this bake. Breadlify shows:
            </p>

            <ul className="space-y-2 ml-9 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Current health status of each starter</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>When each starter was last fed</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Warnings if a starter is already scheduled for another bake</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Flour type and texture information</span>
              </li>
            </ul>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded p-3 mt-3 ml-9">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-xs">
                  Make sure your starter will be healthy and fed at the right time for your bake!
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 border-l-4 border-pink-400">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-pink-500/20 rounded-full flex items-center justify-center text-sm text-pink-400 font-bold">5</span>
              Starter Feeding Schedule
            </h4>

            <p className="text-white/70 text-sm mb-3 ml-9">
              Breadlify automatically creates a feeding schedule for your starter based on:
            </p>

            <ul className="space-y-2 ml-9 text-sm text-white/70 mb-4">
              <li className="flex items-start gap-2">
                <Wheat className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>Your starter's feeding ratio</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>When you need to knead the dough</span>
              </li>
              <li className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>Your starter's current state and last feeding</span>
              </li>
            </ul>

            <div className="bg-white/5 rounded-lg p-3 ml-9">
              <p className="text-white/90 font-medium text-sm mb-2">Customization</p>
              <p className="text-white/60 text-xs mb-2">
                You can adjust the suggested feeding schedule:
              </p>
              <ul className="text-white/60 text-xs space-y-1">
                <li>• Add or remove feedings</li>
                <li>• Change feeding times</li>
                <li>• Adjust feeding ratios for specific feeds</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section id="timeline" title="Understanding the Timeline">
        <p className="text-white/80 mb-4">
          Once your session is created, Breadlify provides a complete timeline with all important milestones:
        </p>

        <div className="space-y-3">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Wheat className="w-5 h-5 text-amber-400" />
              <h4 className="text-white font-medium">Starter Feedings</h4>
            </div>
            <p className="text-white/70 text-sm">
              Scheduled feeding times to ensure your starter is at peak activity when you need it.
              You'll receive notifications before each feeding.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-blue-400" />
              <h4 className="text-white font-medium">Kneading Time</h4>
            </div>
            <p className="text-white/70 text-sm">
              When to mix your dough. This is when bulk fermentation begins. Make sure your starter
              is at peak (doubled in size, bubbly, passing the float test).
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-green-400" />
              <h4 className="text-white font-medium">Shaping Time</h4>
            </div>
            <p className="text-white/70 text-sm">
              When bulk fermentation ends and it's time to shape your dough. Look for 50-100% volume increase
              and jiggly texture.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Flame className="w-5 h-5 text-orange-400" />
              <h4 className="text-white font-medium">Baking Time</h4>
            </div>
            <p className="text-white/70 text-sm">
              Your target time! Preheat your oven 45-60 minutes before. Score your bread and bake.
              Enjoy the aroma of fresh sourdough!
            </p>
          </div>
        </div>
      </Section>

      <Section id="tracking" title="Tracking Active Sessions">
        <p className="text-white/80 mb-4">
          Your active baking sessions appear in multiple places:
        </p>

        <ul className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Home Dashboard</span>
              Shows upcoming sessions with countdown timers
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Baking In Progress Widget</span>
              Appears when a session is actively in progress, showing current stage and next milestone
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Notifications</span>
              Receive email and in-app notifications at each milestone
            </div>
          </li>
        </ul>
      </Section>

      <Section id="managing" title="Managing Sessions">
        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Viewing Session Details</h4>
            <p className="text-white/70 text-sm">
              Click on any session to view its complete timeline, all milestones, and starter feeding schedule.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Canceling a Session</h4>
            <p className="text-white/70 text-sm">
              If plans change, you can cancel a session from the session details. This will stop all notifications
              and free up your starter for other bakes.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Completing a Session</h4>
            <p className="text-white/70 text-sm mb-2">
              Once you've baked your bread, mark the session as complete. You'll be prompted to:
            </p>
            <ul className="text-white/60 text-xs space-y-1 ml-4">
              <li>• Upload photos of your bread</li>
              <li>• Add notes about the bake</li>
              <li>• Rate the result</li>
              <li>• Create an analysis (premium feature)</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section id="tips" title="Pro Tips for Perfect Timing">
        <ul className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Plan ahead</span>
              Great bread can't be rushed. Give yourself at least 24 hours for best results with cold fermentation methods.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Don't skip starter prep</span>
              Follow the feeding schedule carefully. A well-fed, active starter is crucial for good rise and flavor.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Temperature affects timing</span>
              Warmer = faster fermentation. If your kitchen is warm, fermentation may finish earlier than scheduled.
              Watch your dough, not just the clock.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Enable notifications</span>
              Make sure email and in-app notifications are enabled so you never miss a critical timing milestone.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Overlap is okay</span>
              You can have multiple sessions running simultaneously if you have multiple starters or are planning
              multiple bakes for different times.
            </div>
          </li>
        </ul>
      </Section>
    </div>
  );
}
